import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    LuChevronDown, LuChevronLeft, LuChevronRight, LuChevronUp, LuChevronsUpDown,
    LuCircleCheck, LuCircleDashed, LuEye, LuLoaderCircle, LuPencil, LuPlus, LuSearch, LuTrash2,
} from 'react-icons/lu';
import aviso, { SEM_SERVIDOR } from '../lib/aviso';
import PageHeader from '../components/PageHeader';
import WhatsAppConnectionGuard from '../components/WhatsAppConnectionGuard';
import {
    CardConfirm, FiltroPill, ListCard, ListCards, ListRow, PerPage, RowAction, RowActions, RowConfirm,
} from '../components/ListKit';
import { useCelular } from '../hooks/useCelular';
import { PERMISSOES, temPermissao } from '../config/permissions';
import { CampaignService, type CampanhaResumo, type CampanhaStatus } from '../services/CampaignService';
import './Disparador.css';

/**
 * Disparador — a listagem de campanhas.
 *
 * Desenho fechado em docs/designsystem/propostas/campanhas.html (18/09/2026).
 * A campanha abre em página própria (`CampanhaPage`), que é onde vivem os
 * quatro passos e, depois do disparo, o resultado.
 *
 * O que a linha carrega é identidade e estado — mais os três números que
 * separam uma campanha da outra. "Entregues" fica no funil da página: entre
 * campanhas ele quase sempre repete o enviado.
 */

/* Larguras conferidas com o cabeçalho renderizado: o rótulo carrega a seta de
   ordenação, então "Respostas" pede 112px para não virar "RESPOST…". */
const COLUNAS = '1fr 210px 118px 100px 92px 112px 110px 78px';
const POR_PAGINA = [25, 50, 100] as const;
const CHAVE_RECORTE = 'lemeai_disparador_recorte';

type Coluna = 'nome' | 'template' | 'status' | 'disparadas' | 'lidas' | 'respostas' | 'criada';

/** Texto sobe (A→Z); número e data descem. A primeira ordenação é a natural. */
const DIRECAO_PADRAO: Record<Coluna, 'asc' | 'desc'> = {
    nome: 'asc', template: 'asc', status: 'asc',
    disparadas: 'desc', lidas: 'desc', respostas: 'desc', criada: 'desc',
};

interface Recorte {
    busca: string;
    status: string | null;
    ordem: string;
    porPagina: number;
    pagina: number;
}

const RECORTE_VAZIO: Recorte = {
    busca: '', status: null, ordem: 'criada:desc', porPagina: 25, pagina: 1,
};

/** O recorte de trabalho não se perde ao abrir uma campanha e voltar. */
const lerRecorte = (): Recorte => {
    try {
        const cru = sessionStorage.getItem(CHAVE_RECORTE);
        return cru ? { ...RECORTE_VAZIO, ...JSON.parse(cru) } : RECORTE_VAZIO;
    } catch {
        return RECORTE_VAZIO;
    }
};

const dataCurta = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const STATUS_ICONE: Record<CampanhaStatus, typeof LuCircleDashed> = {
    Rascunho: LuCircleDashed,
    Enviando: LuLoaderCircle,
    Finalizada: LuCircleCheck,
};

const DisparadorPage = () => {
    const navegar = useNavigate();
    /* A tela abre com `marketing_disparador`; criar, editar, excluir e disparar
       pedem `marketing_disparador.gerenciar` (a API recusa sem a chave). */
    const podeGerenciar = temPermissao(PERMISSOES.marketingDisparadorGerenciar);
    const celular = useCelular();

    const [campanhas, setCampanhas] = useState<CampanhaResumo[]>([]);
    const [carregando, setCarregando] = useState(true);

    const inicial = useMemo(lerRecorte, []);
    const [busca, setBusca] = useState(inicial.busca);
    const [status, setStatus] = useState<string | null>(inicial.status);
    const [ordem, setOrdem] = useState(inicial.ordem);
    const [porPagina, setPorPagina] = useState(inicial.porPagina);
    const [pagina, setPagina] = useState(inicial.pagina);

    const [aConfirmar, setAConfirmar] = useState<number | null>(null);
    const [apagando, setApagando] = useState(false);

    useEffect(() => {
        const recorte: Recorte = { busca, status, ordem, porPagina, pagina };
        sessionStorage.setItem(CHAVE_RECORTE, JSON.stringify(recorte));
    }, [busca, status, ordem, porPagina, pagina]);

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const res = await CampaignService.buscarResumo();
            if (res.sucesso) setCampanhas(res.dados ?? []);
            else aviso.erro(res.mensagem || 'Não foi possível carregar as campanhas.');
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setCarregando(false);
        }
    }, []);

    useEffect(() => { carregar(); }, [carregar]);

    /* ── filtro, ordem e página ─────────────────────────────── */

    const opcoesStatus = useMemo(() => {
        const conta = new Map<string, number>();
        for (const c of campanhas) conta.set(c.campanhaStatus, (conta.get(c.campanhaStatus) ?? 0) + 1);
        return [...conta.entries()].map(([valor, total]) => ({ valor, total }));
    }, [campanhas]);

    const filtradas = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        return campanhas.filter(c => {
            const casaBusca = !termo
                || c.campanhaNome.toLowerCase().includes(termo)
                || c.campanhaTemplateNome.toLowerCase().includes(termo);
            const casaStatus = !status || c.campanhaStatus === status;
            return casaBusca && casaStatus;
        });
    }, [campanhas, busca, status]);

    const [colunaOrdem, direcao] = ordem.split(':') as [Coluna, 'asc' | 'desc'];

    const ordenadas = useMemo(() => {
        const valor = (c: CampanhaResumo): string | number => {
            switch (colunaOrdem) {
                case 'template': return c.campanhaTemplateNome.toLowerCase();
                case 'status': return c.campanhaStatus.toLowerCase();
                case 'disparadas': return c.totalDisparado;
                case 'lidas': return c.totalLidas ?? 0;
                case 'respostas': return c.totalComInteracao;
                case 'criada': return new Date(c.campanhaCreatedat).getTime() || 0;
                default: return c.campanhaNome.toLowerCase();
            }
        };
        const sinal = direcao === 'asc' ? 1 : -1;
        return [...filtradas].sort((a, b) => {
            const va = valor(a); const vb = valor(b);
            if (va === vb) return a.campanhaNome.localeCompare(b.campanhaNome, 'pt-BR');
            // Vazio vai para o fim nos dois sentidos.
            if (va === '' ) return 1;
            if (vb === '') return -1;
            return va < vb ? -sinal : sinal;
        });
    }, [filtradas, colunaOrdem, direcao]);

    const totalPaginas = Math.max(1, Math.ceil(ordenadas.length / porPagina));
    const paginaAtual = Math.min(pagina, totalPaginas);
    const inicio = (paginaAtual - 1) * porPagina;
    const visiveis = ordenadas.slice(inicio, inicio + porPagina);

    // Mudar um filtro volta para a pagina 1 - mas nao na montagem, senao a
    // pagina restaurada da sessao se perde.
    const primeiraRodada = useRef(true);
    useEffect(() => {
        if (primeiraRodada.current) { primeiraRodada.current = false; return; }
        setPagina(1);
    }, [busca, status, porPagina]);

    const ordenarPor = (col: Coluna) => {
        setOrdem(col === colunaOrdem
            ? `${col}:${direcao === 'asc' ? 'desc' : 'asc'}`
            : `${col}:${DIRECAO_PADRAO[col]}`);
    };

    const cabecalho = (col: Coluna, rotulo: string, numerica = false) => {
        const ativo = colunaOrdem === col;
        const Seta = !ativo ? LuChevronsUpDown : direcao === 'asc' ? LuChevronUp : LuChevronDown;
        return (
            <button type="button"
                className={`lk-head lk-head-sort ${ativo ? 'is-on' : ''} ${numerica ? 'dsp-num' : ''}`}
                onClick={() => ordenarPor(col)}
                aria-sort={ativo ? (direcao === 'asc' ? 'ascending' : 'descending') : 'none'}
                title={`Ordenar por ${rotulo.toLowerCase()}`}>
                <span>{rotulo}</span><Seta size={12} aria-hidden="true" />
            </button>
        );
    };

    /* ── ações ──────────────────────────────────────────────── */

    const excluir = async (c: CampanhaResumo) => {
        setApagando(true);
        try {
            const res = await CampaignService.remove(c.campanhaId);
            if (res.sucesso) {
                aviso.ok('Campanha removida.');
                setCampanhas(antes => antes.filter(x => x.campanhaId !== c.campanhaId));
                setAConfirmar(null);
            } else {
                aviso.erro(res.mensagem || 'Não foi possível remover a campanha.');
            }
        } catch {
            aviso.erro(SEM_SERVIDOR);
        } finally {
            setApagando(false);
        }
    };

    const abrir = (c: CampanhaResumo) => navegar(`/campanhas/${c.campanhaId}`);

    /* ── partes ─────────────────────────────────────────────── */

    const Etiqueta = ({ s }: { s: CampanhaStatus }) => {
        const Icone = STATUS_ICONE[s] ?? LuCircleDashed;
        return (
            <span className={`dsp-st is-${s.toLowerCase()}`}>
                <Icone size={11} aria-hidden="true" />{s}
            </span>
        );
    };

    const rascunho = (c: CampanhaResumo) => c.campanhaStatus === 'Rascunho';
    /* Em envio o disparo corre no servidor: a lista mostra quanto já saiu do total
       e não oferece excluir (o back recusa — os da fila ficariam órfãos). */
    const enviando = (c: CampanhaResumo) => c.campanhaStatus === 'Enviando';
    const disparos = (c: CampanhaResumo) => enviando(c)
        ? `${c.totalDisparado} de ${c.totalDisparado + (c.totalPendentes ?? 0)}`
        : String(c.totalDisparado);

    const contagem = carregando ? 'Carregando…' : (() => {
        const r = campanhas.filter(c => c.campanhaStatus === 'Rascunho').length;
        const f = campanhas.filter(c => c.campanhaStatus === 'Finalizada').length;
        const partes = [`${campanhas.length} ${campanhas.length === 1 ? 'campanha' : 'campanhas'}`];
        if (r > 0) partes.push(`${r} ${r === 1 ? 'rascunho' : 'rascunhos'}`);
        if (f > 0) partes.push(`${f} ${f === 1 ? 'finalizada' : 'finalizadas'}`);
        return partes.join(' · ');
    })();

    const acoes = podeGerenciar ? (
        <button type="button" className="lk-btn is-primary" onClick={() => navegar('/campanhas/nova')}>
            <LuPlus size={14} /> Nova campanha
        </button>
    ) : undefined;

    const vazioDeVerdade = !carregando && campanhas.length === 0;
    const semResultado = !carregando && campanhas.length > 0 && ordenadas.length === 0;

    /* Os dois avisos de "nada aqui" e o rodapé de paginação servem à tabela (no
       computador) e aos cartões (no celular) — por isso saem do meio do JSX. */
    const listaVazia = (
        <>
            {/* O vazio é irmão do trilho, não linha dentro dele: é o que
                deixa a moldura centrar o aviso na altura do cartão. */}
            {vazioDeVerdade && (
                <div className="lk-blank">
                    <p className="lk-blank-title">Nenhuma campanha ainda</p>
                    <p className="lk-blank-text">
                        Uma campanha junta um público e um template aprovado na Meta,
                        e dispara a mensagem por WhatsApp.
                    </p>
                    {podeGerenciar && (
                        <button type="button" className="lk-btn is-primary"
                            onClick={() => navegar('/campanhas/nova')}>
                            <LuPlus size={14} /> Nova campanha
                        </button>
                    )}
                </div>
            )}

            {semResultado && (
                <div className="lk-blank">
                    <p className="lk-blank-title">Nenhuma campanha encontrada</p>
                    <p className="lk-blank-text">Tente outro termo ou limpe o filtro de status.</p>
                </div>
            )}
        </>
    );

    const rodape = (
        <div className="lk-foot">
            <PerPage value={porPagina} options={POR_PAGINA} onChange={setPorPagina} />
            <span className="lk-range">
                {inicio + 1}–{Math.min(inicio + porPagina, ordenadas.length)} de {ordenadas.length}
            </span>
            <span className="lk-pages">
                <button type="button" className="lk-page" aria-label="Página anterior"
                    disabled={paginaAtual <= 1} onClick={() => setPagina(p => p - 1)}>
                    <LuChevronLeft size={14} />
                </button>
                <span className="lk-page is-current">{paginaAtual}</span>
                <span className="lk-muted">de {totalPaginas}</span>
                <button type="button" className="lk-page" aria-label="Próxima página"
                    disabled={paginaAtual >= totalPaginas} onClick={() => setPagina(p => p + 1)}>
                    <LuChevronRight size={14} />
                </button>
            </span>
        </div>
    );

    return (
        <WhatsAppConnectionGuard>
            <div className="page-shell dsp-screen lk-screen">
                <PageHeader
                    title="Disparador"
                    subtitle="Campanhas de WhatsApp com template aprovado na Meta."
                    /* Sem campanha nenhuma, a porta é a do aviso, no meio da tela. */
                    actions={vazioDeVerdade ? undefined : acoes}
                />

                <div className="page-body lk-fill">
                    <div className="lk-filters">
                        <label className="lk-search">
                            <LuSearch size={14} />
                            <input
                                type="search"
                                placeholder="Buscar por nome ou template…"
                                value={busca}
                                onChange={e => setBusca(e.target.value)}
                            />
                        </label>

                        <FiltroPill rotulo="Status" valor={status} opcoes={opcoesStatus} onChange={setStatus} />

                        <span className="lk-count">{contagem}</span>
                    </div>

                    {/* Celular: a grade de oito colunas não cabe em 358px, então a
                        linha vira cartão — nome, template e o estado com os números
                        que separam uma campanha da outra. O funil fica na página. */}
                    {celular ? (
                        <>
                            <ListCards>
                                {carregando
                                    ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : visiveis.map(c => (
                                        aConfirmar === c.campanhaId ? (
                                            <CardConfirm key={c.campanhaId}
                                                question={`Excluir a campanha «${c.campanhaNome}»?`}
                                                busy={apagando}
                                                onCancel={() => setAConfirmar(null)}
                                                onConfirm={() => excluir(c)} />
                                        ) : (
                                            <ListCard
                                                key={c.campanhaId}
                                                titulo={c.campanhaNome}
                                                apoio={<span className="dsp-tpl">{c.campanhaTemplateNome}</span>}
                                                estado={
                                                    <>
                                                        <Etiqueta s={c.campanhaStatus} />
                                                        {/* Rascunho não disparou nada: número zerado ali
                                                            seria lido como campanha que falhou. */}
                                                        {!rascunho(c) && (
                                                            <span>
                                                                · {disparos(c)} disparos
                                                                · {c.totalLidas ?? 0} lidas
                                                                · {c.totalComInteracao} respostas
                                                            </span>
                                                        )}
                                                    </>
                                                }
                                                label={rascunho(c)
                                                    ? `Editar ${c.campanhaNome}`
                                                    : `Ver o resultado de ${c.campanhaNome}`}
                                                onOpen={() => abrir(c)}
                                                acoes={podeGerenciar && !enviando(c) ? (
                                                    <RowAction icon={<LuTrash2 size={15} />} danger
                                                        label={`Excluir ${c.campanhaNome}`}
                                                        onClick={() => setAConfirmar(c.campanhaId)} />
                                                ) : undefined}
                                            />
                                        )
                                    ))}
                            </ListCards>
                            {listaVazia}
                        </>
                    ) : (
                    <div className="lk-table">
                        <div className="lk-scroll">
                            <div className="lk-row" style={{ gridTemplateColumns: COLUNAS }}>
                                {cabecalho('nome', 'Campanha')}
                                {cabecalho('template', 'Template')}
                                {cabecalho('status', 'Status')}
                                {/* "Disparos", não "Enviadas": o número conta toda tentativa já
                                    processada, falha incluída — o que ainda está na fila fica fora. */}
                                {cabecalho('disparadas', 'Disparos', true)}
                                {cabecalho('lidas', 'Lidas', true)}
                                {cabecalho('respostas', 'Respostas', true)}
                                {cabecalho('criada', 'Criada')}
                                <div className="lk-head lk-head-acts">Ações</div>
                            </div>

                            <div className="lk-body">
                                {carregando
                                    ? Array.from({ length: 8 }).map((_, i) => <div key={i} className="lk-skeleton" />)
                                    : visiveis.map(c => (
                                        aConfirmar === c.campanhaId ? (
                                            <RowConfirm key={c.campanhaId}
                                                question={`Excluir a campanha «${c.campanhaNome}»?`}
                                                busy={apagando}
                                                onCancel={() => setAConfirmar(null)}
                                                onConfirm={() => excluir(c)} />
                                        ) : (
                                            <ListRow key={c.campanhaId} columns={COLUNAS}>
                                                <span className="lk-cell lk-strong">{c.campanhaNome}</span>
                                                <span className="lk-cell">
                                                    <span className="dsp-tpl">{c.campanhaTemplateNome}</span>
                                                </span>
                                                <span className="lk-cell"><Etiqueta s={c.campanhaStatus} /></span>
                                                <span className={`lk-cell dsp-num ${rascunho(c) ? 'is-vazio' : ''}`}>
                                                    {rascunho(c) ? '—' : disparos(c)}
                                                </span>
                                                <span className={`lk-cell dsp-num ${rascunho(c) ? 'is-vazio' : ''}`}>
                                                    {rascunho(c) ? '—' : (c.totalLidas ?? 0)}
                                                </span>
                                                <span className={`lk-cell dsp-num ${rascunho(c) ? 'is-vazio' : ''}`}>
                                                    {rascunho(c) ? '—' : c.totalComInteracao}
                                                </span>
                                                <span className="lk-cell lk-muted">{dataCurta(c.campanhaCreatedat)}</span>
                                                <RowActions>
                                                    {/* Lápis onde dá para editar, olho onde não dá: um olho numa
                                                        linha de rascunho prometeria leitura onde só há formulário. */}
                                                    <RowAction
                                                        icon={rascunho(c) && podeGerenciar ? <LuPencil size={14} /> : <LuEye size={14} />}
                                                        label={rascunho(c) && podeGerenciar
                                                            ? `Editar ${c.campanhaNome}`
                                                            : `Ver o resultado de ${c.campanhaNome}`}
                                                        onClick={() => abrir(c)} />
                                                    {podeGerenciar && !enviando(c) && (
                                                        <RowAction icon={<LuTrash2 size={14} />} danger
                                                            label={`Excluir ${c.campanhaNome}`}
                                                            onClick={() => setAConfirmar(c.campanhaId)} />
                                                    )}
                                                </RowActions>
                                            </ListRow>
                                        )
                                    ))}

                            </div>
                        </div>

                        {listaVazia}
                    </div>
                    )}

                    {!carregando && ordenadas.length > 0 && rodape}
                </div>
            </div>
        </WhatsAppConnectionGuard>
    );
};

export default DisparadorPage;
